/**
 * The fills grouped into measures: one per scoring section name (else the form's name), each with
 * its scores oldest first. A section none of whose questions was answered is left out; so is a
 * fill on the same day from the same source twice (the later one counts).
 */
export function groupMeasures(fills) {
    const byKey = new Map();
    const sorted = [...fills].sort((a, b) => a.date.localeCompare(b.date));
    for (const f of sorted) {
        for (const s of f.sections) {
            if (!s.answered)
                continue;
            const key = (s.name || f.formName).trim();
            if (!key)
                continue;
            let m = byKey.get(key);
            if (!m) {
                m = { key, max: s.max, bands: s.bands ?? [], scores: [] };
                byKey.set(key, m);
            }
            m.max = Math.max(m.max, s.max);
            if (s.bands?.length)
                m.bands = s.bands;
            const row = {
                id: f.id,
                date: f.date,
                score: s.score,
                band: s.band?.label ?? null,
                severity: s.band?.severity ?? null,
                alert: s.message,
                source: f.source,
            };
            const same = m.scores.findIndex((x) => x.date === row.date && x.source === row.source);
            if (same >= 0)
                m.scores[same] = row;
            else
                m.scores.push(row);
        }
    }
    return [...byKey.values()]
        .filter((m) => m.scores.length)
        .sort((a, b) => a.key.localeCompare(b.key));
}
/** The band a score falls in (bands as the scoring has them, min..max). */
export function bandFor(bands, score) {
    return bands.find((b) => score >= b.min && score <= b.max) ?? null;
}
/** M/D/YYYY → MM/DD/YYYY for the note's sentences. */
const mdy = (iso) => {
    const [y, m, d] = iso.split('-');
    return y && m && d ? `${m}/${d}/${y}` : iso;
};
const bandText = (s) => (s.band ? s.band.toLowerCase() : '');
/**
 * V81 measureLine: the sentence the note gets, e.g. "PHQ-9 12 (moderate) on 10/08/2026, down from
 * 15 on 10/01/2026."
 */
export function measureLine(m) {
    const s = m.scores;
    const last = s[s.length - 1];
    if (!last)
        return '';
    const prev = s[s.length - 2];
    let t = `${m.key} ${last.score}${bandText(last) ? ` (${bandText(last)})` : ''} on ${mdy(last.date)}`;
    if (prev)
        t +=
            last.score < prev.score
                ? `, down from ${prev.score} on ${mdy(prev.date)}`
                : last.score > prev.score
                    ? `, up from ${prev.score} on ${mdy(prev.date)}`
                    : `, unchanged from ${mdy(prev.date)}`;
    return `${t}.`;
}
/**
 * V81 measureTrendLine: the scores in a range as one sentence, e.g. "PHQ-9 18, 15, 12 from
 * 09/01/2026 to 10/08/2026 (moderately severe to moderate)."
 */
export function measureTrendLine(m, scores = m.scores) {
    if (!scores.length)
        return '';
    const a = scores[0];
    const b = scores[scores.length - 1];
    const b1 = bandText(a);
    const b2 = bandText(b);
    const bands = b1 && b2 ? ` (${b1 === b2 ? b2 : `${b1} to ${b2}`})` : '';
    return `${m.key} ${scores.map((p) => p.score).join(', ')} from ${mdy(a.date)} to ${mdy(b.date)}${bands}.`;
}
/** V81 RANGES: the scores in the last N months counted back from the newest (0 = all). */
export function scoresWithin(m, months) {
    const last = m.scores[m.scores.length - 1];
    if (!months || !last)
        return m.scores.slice();
    const [y, mo, d] = last.date.split('-').map(Number);
    const c = new Date(Date.UTC(y, mo - 1 - months, d));
    const cut = c.toISOString().slice(0, 10);
    return m.scores.filter((p) => p.date >= cut);
}
//# sourceMappingURL=measures.js.map